/**
 * File: bankLines.ts
 * Path: packages/config/src/accounting/bankLines.ts
 * Description: What is kept for each line of an imported bank statement: a
 * stable id so the same line is never saved twice (statements may overlap),
 * the cleaned "who" of the line (the payee), the suggestion of where it goes,
 * and the stored shapes the server writes and the admin reads. The server and
 * the browser both use these functions so they always agree.
 */

import type { JournalDocument } from "./accountingTypes";
import type { BankLine } from "./bankStatement";
import { isBooksDate } from "./ledger";

export const BANK_COLLECTIONS = {
  lines: "bankLines",
  statements: "bankStatements",
  rules: "bankRules",
  reconciliations: "reconciliations",
} as const;

/** The company's and personal books both keep their bank account at this code. */
export const BANK_ACCOUNT_CODE = "1000";

/** The most lines one upload may carry (a year of busy business banking is about 700). */
export const BANK_UPLOAD_LIMIT = 1500;

export type BankLineStatus = "new" | "posted" | "ignored" | "inBooks";

/** Where the books think a line goes, and how sure they are. */
export interface BankSuggestion {
  accountId: string;
  reason: string;
  /** Seen three or more times, nine in ten the same way. */
  confident: boolean;
  /** What your rule renames the line to, when it has a name. */
  name?: string;
}

export interface BankLineRecord {
  id: string;
  statementId: string;
  date: string;
  description: string;
  amountCents: number;
  balanceCents: number | null;
  status: BankLineStatus;
  /** The journal this line made, or the one it was found in already. */
  journalId: string | null;
  journalNumber: number | null;
  /** The account chosen when it was posted. */
  accountId: string | null;
  /** What the owner calls this line ("" until renamed). The bank's own words stay in `description`. */
  name: string;
  payee: string;
  suggestion: BankSuggestion | null;
}

export interface BankStatementRecord {
  id: string;
  fileName: string;
  bank: string;
  from: string | null;
  to: string | null;
  openingCents: number | null;
  closingCents: number | null;
  lineCount: number;
  /** Lines saved for sorting. */
  addedCount: number;
  /** Lines saved earlier, left alone. */
  repeatCount: number;
  /** Lines found already in the books. */
  inBooksCount: number;
  /** What the books held in the bank on the statement's last day, and whether that equals the bank's closing balance. */
  booksBalanceCents: number | null;
  reconciled: boolean;
  /** The statement PDF kept as the source document, when there was one. */
  document: JournalDocument | null;
  uploadedBy: string;
  uploadedAt: number | null;
}

/** Learned payee → account counts, one document per payee and direction. */
export interface BankRule {
  id: string;
  payee: string;
  /** How many times each account was chosen. */
  accounts: Record<string, number>;
}

/** A rule the owner wrote: "if the line says X, it goes to this account". Kept beside the learned payees. */
export interface CustomBankRule {
  id: string;
  /** Words the bank line's description must contain, any case. */
  contains: string;
  /** Money in, money out, or either. */
  direction: "in" | "out" | "any";
  accountId: string;
  /** What to call lines this rule catches ("" keeps the bank's words). */
  name: string;
  /** Counts as "usual": included when the usual lines are posted together. Otherwise it only suggests. */
  auto: boolean;
  /** Rules are tried from the lowest number up; the first that fits wins. */
  order: number;
}

/** One finished reconciliation of the bank account: the entries ticked off against a statement's closing balance. */
export interface ReconciliationRecord {
  id: string;
  statementId: string | null;
  from: string;
  to: string;
  closingCents: number;
  /** Bank movement of every entry reconciled so far, this one included. Equals the closing balance. */
  clearedCents: number;
  journalIds: string[];
  reconciledBy: string;
  reconciledAt: number | null;
  /** False once undone; kept on file. */
  active: boolean;
}

type Raw = Record<string, unknown>;
const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const int = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : 0);
const intOrNull = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : null);
const millis = (v: unknown): number | null => {
  if (typeof v === "number") return v;
  if (v && typeof v === "object" && "toMillis" in v && typeof (v as { toMillis: unknown }).toMillis === "function") {
    return (v as { toMillis: () => number }).toMillis();
  }
  return null;
};

/** A small, stable string hash (cyrb53), written in base 36. */
const hash = (text: string): string => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

const squash = (text: string) => text.toUpperCase().replace(/\s+/g, " ").trim();

/**
 * An id for each line that does not change between uploads: the same date,
 * amount, balance and words always give the same id. Two truly identical lines
 * in one file (two R20 coffees on one day) get -2, -3 and so on.
 */
export const bankLineIds = (lines: readonly BankLine[]): string[] => {
  const seen = new Map<string, number>();
  return lines.map((l) => {
    const base = hash(`${l.date}|${l.amountCents}|${l.balanceCents ?? ""}|${squash(l.description)}`);
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return n === 1 ? `b-${base}` : `b-${base}-${n}`;
  });
};

const NOISE =
  /\b(FNB APP PAYMENT (TO|FROM)|FNB APP PRE-?PAID|POS PURCHASE|FUEL PURCHASE|PAYSHAP(ID)? (ACCOUNT )?(OFF-US|CREDIT|DEBIT)?|PAYSHAP(ID)?|MAGTAPE (CREDIT|DEBIT)|INT-BANKING PMT (FRM|TO)|IB PAYMENT (TO|FROM)|DEBIT ORDER|CASH WITHDRAWAL|ATM|PAYMENT (TO|FROM)|CREDIT CARD|OFF-US|ACC)\b/g;

/** "POS Purchase 25.00 Lovable 485442*9689 01 Jun" becomes "LOVABLE". */
export const payeeOf = (description: string): string => {
  const cleaned = squash(description)
    .replace(NOISE, " ")
    .replace(/\d{4,}\*\d{3,}/g, " ")
    .replace(/\b\d{1,2} (JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b/g, " ")
    .replace(/[*|]/g, " ");
  const words = cleaned
    .split(" ")
    .filter((w) => w.length > 1 && !/\d/.test(w));
  return words.slice(0, 3).join(" ") || squash(description).slice(0, 30);
};

/** The id of the learned-rule document for a payee, split by money in and money out. */
export const payeeRuleId = (payee: string, amountCents: number): string => {
  const slug = payee.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "unknown";
  return `${amountCents >= 0 ? "in" : "out"}-${slug}`;
};

/** The account a payee usually goes to, from what was chosen before. */
export const suggestFromRule = (rule: BankRule | undefined): BankSuggestion | null => {
  if (!rule) return null;
  const entries = Object.entries(rule.accounts).filter(([, n]) => n > 0);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  if (total === 0) return null;
  const [accountId, count] = entries.sort((a, b) => b[1] - a[1])[0];
  const confident = total >= 3 && count / total >= 0.9;
  return {
    accountId,
    confident,
    reason: confident
      ? `Went here ${count} of ${total} times before.`
      : total === 1
        ? "Went here last time."
        : `Went here ${count} of ${total} times before.`,
  };
};

/** The quote reference the client typed, e.g. "IDK-C012-02-DEP", if the line carries one. */
export const paymentReferenceIn = (description: string): string | null => {
  const m = /\bIDK-C\d{3,}-\d{2}(?:-[A-Z0-9]+)?\b/i.exec(description);
  return m ? m[0].toUpperCase() : null;
};

export const toBankLineRecord = (id: string, data: Raw | undefined | null): BankLineRecord | null => {
  const r = data ?? {};
  const status = r.status;
  if (status !== "new" && status !== "posted" && status !== "ignored" && status !== "inBooks") return null;
  if (!isBooksDate(r.date)) return null;
  const s = (r.suggestion ?? null) as Raw | null;
  return {
    id,
    statementId: str(r.statementId),
    date: r.date,
    description: str(r.description),
    amountCents: int(r.amountCents),
    balanceCents: intOrNull(r.balanceCents),
    status,
    journalId: typeof r.journalId === "string" ? r.journalId : null,
    journalNumber: intOrNull(r.journalNumber),
    accountId: typeof r.accountId === "string" ? r.accountId : null,
    name: str(r.name),
    payee: str(r.payee),
    suggestion:
      s && typeof s.accountId === "string"
        ? { accountId: s.accountId, reason: str(s.reason), confident: s.confident === true }
        : null,
  };
};

const toDocument = (v: unknown): JournalDocument | null => {
  const d = (v ?? null) as Raw | null;
  return d && typeof d.path === "string"
    ? { path: d.path, name: str(d.name), contentType: str(d.contentType), size: int(d.size) }
    : null;
};

export const toBankStatementRecord = (id: string, data: Raw | undefined | null): BankStatementRecord => {
  const r = data ?? {};
  return {
    id,
    fileName: str(r.fileName),
    bank: str(r.bank),
    from: isBooksDate(r.from) ? r.from : null,
    to: isBooksDate(r.to) ? r.to : null,
    openingCents: intOrNull(r.openingCents),
    closingCents: intOrNull(r.closingCents),
    lineCount: int(r.lineCount),
    addedCount: int(r.addedCount),
    repeatCount: int(r.repeatCount),
    inBooksCount: int(r.inBooksCount),
    booksBalanceCents: intOrNull(r.booksBalanceCents),
    reconciled: r.reconciled === true,
    document: toDocument(r.document),
    uploadedBy: str(r.uploadedBy),
    uploadedAt: millis(r.uploadedAt),
  };
};

export const isCustomRuleDoc = (data: Raw | undefined | null): boolean => (data ?? {}).kind === "rule";

export const toCustomBankRule = (id: string, data: Raw | undefined | null): CustomBankRule | null => {
  const r = data ?? {};
  if (r.kind !== "rule" || typeof r.accountId !== "string" || typeof r.contains !== "string") return null;
  const direction = r.direction === "in" || r.direction === "out" ? r.direction : "any";
  return { id, contains: r.contains, direction, accountId: r.accountId, name: str(r.name), auto: r.auto === true, order: int(r.order) };
};

export const toReconciliationRecord = (id: string, data: Raw | undefined | null): ReconciliationRecord | null => {
  const r = data ?? {};
  if (!isBooksDate(r.from) || !isBooksDate(r.to)) return null;
  return {
    id,
    statementId: typeof r.statementId === "string" ? r.statementId : null,
    from: r.from,
    to: r.to,
    closingCents: int(r.closingCents),
    clearedCents: int(r.clearedCents),
    journalIds: Array.isArray(r.journalIds) ? r.journalIds.filter((v): v is string => typeof v === "string") : [],
    reconciledBy: str(r.reconciledBy),
    reconciledAt: millis(r.reconciledAt),
    active: r.active !== false,
  };
};

/** The first of the owner's rules (lowest order) that fits this line, or null. */
export const matchCustomRule = (
  line: { description: string; amountCents: number },
  rules: readonly CustomBankRule[],
): CustomBankRule | null => {
  const text = squash(line.description);
  const sorted = [...rules].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  return (
    sorted.find((rule) => {
      const needle = squash(rule.contains);
      if (needle === "" || !text.includes(needle)) return false;
      return rule.direction === "any" || (rule.direction === "in" ? line.amountCents > 0 : line.amountCents < 0);
    }) ?? null
  );
};

export const suggestFromCustomRule = (rule: CustomBankRule | null): BankSuggestion | null =>
  rule
    ? {
        accountId: rule.accountId,
        confident: rule.auto,
        reason: `Your rule: the line says "${rule.contains}".`,
        ...(rule.name ? { name: rule.name } : {}),
      }
    : null;

export const toBankRule = (id: string, data: Raw | undefined | null): BankRule => {
  const r = data ?? {};
  const accounts: Record<string, number> = {};
  for (const [k, v] of Object.entries((r.accounts ?? {}) as Raw)) {
    if (typeof v === "number" && v > 0) accounts[k] = Math.trunc(v);
  }
  return { id, payee: str(r.payee), accounts };
};

/** Looks for a journal on the bank account the same day for the same amount: the line is already in the books. */
export interface BankJournalFact {
  journalId: string;
  number: number;
  date: string;
  /** What the bank account moved by in this journal (debit positive). */
  bankCents: number;
  /** The other account in a two-line journal, if there is just one. */
  otherAccountId: string | null;
  /** Already tied to a bank line. */
  linked: boolean;
}

export const findInBooks = (
  line: BankLine,
  facts: readonly BankJournalFact[],
  taken: ReadonlySet<string>,
  toleranceDays = 3,
): BankJournalFact | null => {
  const day = (iso: string) => Math.round(Date.parse(`${iso}T00:00:00Z`) / 86_400_000);
  const target = day(line.date);
  let best: BankJournalFact | null = null;
  let bestGap = Infinity;
  for (const f of facts) {
    if (f.linked || taken.has(f.journalId) || f.bankCents !== line.amountCents) continue;
    const gap = Math.abs(day(f.date) - target);
    if (gap <= toleranceDays && gap < bestGap) {
      best = f;
      bestGap = gap;
    }
  }
  return best;
};
